import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
} from '@mui/material';
import { useGetAging } from '../../api/queries';
import { fr } from '../../i18n/fr';
import { formatPaginationLabel } from '../../utils/pagination';

interface Props { storeId: string; }

const AgingTable: React.FC<Props> = ({ storeId }) => {
  const { data, isPending, error } = useGetAging(storeId);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const resolvedData = React.useMemo(() => data ?? [], [data]);

  React.useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(resolvedData.length / rowsPerPage) - 1
    );
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [resolvedData.length, rowsPerPage, page]);

  const paginatedData = React.useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return resolvedData.slice(start, end);
  }, [resolvedData, page, rowsPerPage]);

  const handleChangePage = (
    _event: unknown,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (isPending)
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  if (error)
    return <Alert severity="error">{(error as Error).message || fr.error}</Alert>;
  if (!resolvedData.length) return <Alert severity="info">{fr.noResults}</Alert>;
  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {fr.agingTitle}
      </Typography>
      <TableContainer>
        <Table size="small" aria-label="aging-table">
          <TableHead>
            <TableRow>
              <TableCell>{fr.product}</TableCell>
              <TableCell>Jours d'ancienneté</TableCell>
              <TableCell>Tranche</TableCell>
              <TableCell align="right">{fr.quantity}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((item) => (
              <TableRow key={`${item.product_id}-${item.bucket}`} hover>
                <TableCell>{item.product_id}</TableCell>
                <TableCell>{item.days}</TableCell>
                <TableCell>{item.bucket}</TableCell>
                <TableCell align="right">{item.quantity}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={resolvedData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage={fr.productsPerPage}
        labelDisplayedRows={formatPaginationLabel}
      />
    </Paper>
  );
};

export default AgingTable;
